import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FileViewer from "@/components/FileViewer";
import { formatDateDMY } from "@/lib/dates";
import {
  Search, BookOpen, FileText, Server, Globe, Network, ScrollText, Download, Eye,
} from "lucide-react";

const CATEGORIES = [
  { name: "Government Orders & Circulars", icon: ScrollText, color: "text-brand-orange", source: "circulars" },
  { name: "Website Management", icon: Globe, color: "text-brand-blue", source: "knowledge" },
  { name: "Network Administration", icon: Network, color: "text-brand-green", source: "knowledge" },
  { name: "Software Support", icon: Server, color: "text-brand-orange", source: "knowledge" },
  { name: "E-Governance Systems", icon: BookOpen, color: "text-brand-blue", source: "knowledge" },
];

export default function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [circulars, setCirculars] = useState([]);
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get("/knowledge").then(r => setDocs(r.data)).catch(() => {});
    api.get("/circulars").then(r => setCirculars(r.data)).catch(() => {});
  }, []);

  const filterDocs = (cat) =>
    docs.filter(d =>
      d.category === cat &&
      (q ? (d.title + d.description + d.doc_type).toLowerCase().includes(q.toLowerCase()) : true)
    );

  const filteredCirculars = circulars
    .filter(c =>
      q ? (c.title + (c.summary || "") + (c.category || "")).toLowerCase().includes(q.toLowerCase()) : true
    )
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const countFor = (c) =>
    c.source === "circulars" ? filteredCirculars.length : filterDocs(c.name).length;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Knowledge Base</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Documentation, tutorials & government orders</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              A growing repository of resources curated by KELTA for technical assistants across Kerala.
            </p>
          </div>

          <div className="max-w-xl relative mb-8">
            <Search className="absolute w-4 h-4 left-3 top-3.5 text-muted-foreground" />
            <Input
              className="pl-9 h-11"
              placeholder="Search guides, manuals, government orders…"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="kb-search-input"
            />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              return (
                <Card key={c.name} className="p-4 hover-lift">
                  <Icon className={`w-5 h-5 ${c.color} mb-2`} />
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{countFor(c)} documents</div>
                </Card>
              );
            })}
          </div>

          <Accordion type="multiple" defaultValue={["Government Orders & Circulars"]} className="space-y-3">
            {CATEGORIES.map(c => (
              <AccordionItem key={c.name} value={c.name} className="border border-border rounded-lg bg-card px-4">
                <AccordionTrigger className="hover:no-underline" data-testid={`kb-cat-${c.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
                  <div className="flex items-center gap-3">
                    <c.icon className={`w-4 h-4 ${c.color}`} />
                    <span className="font-semibold">{c.name}</span>
                    <Badge variant="secondary">{countFor(c)}</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {c.source === "circulars" ? (
                    <CircularsList items={filteredCirculars} onView={setViewing} />
                  ) : (
                    <KnowledgeList items={filterDocs(c.name)} />
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </main>
      <Footer />
      <FileViewer
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing?.title}
        url={viewing?.file_url}
      />
    </div>
  );
}

function CircularsList({ items, onView }) {
  if (items.length === 0) {
    return <div className="py-5 text-sm text-muted-foreground text-center">No circulars in this category yet.</div>;
  }
  return (
    <ul className="divide-y divide-border">
      {items.map(c => (
        <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`kb-circular-${c.id}`}>
          <ScrollText className="w-4 h-4 text-brand-orange mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-[10px]">{c.category}</Badge>
              <span className="text-xs text-muted-foreground">{formatDateDMY(c.date)}</span>
            </div>
            <div className="font-medium mt-1">{c.title}</div>
            <div className="text-xs text-muted-foreground">{c.summary}</div>
          </div>
          {c.file_url ? (
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => onView(c)} data-testid={`kb-view-${c.id}`}>
                <Eye className="w-3.5 h-3.5 mr-1" /> View
              </Button>
              <a href={c.file_url} download={(c.title || "circular").replace(/[^a-z0-9._-]+/gi, "_")}>
                <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </a>
            </div>
          ) : (
            <span className="text-[10px] text-muted-foreground flex-shrink-0">No file</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function KnowledgeList({ items }) {
  if (items.length === 0) {
    return <div className="py-5 text-sm text-muted-foreground text-center">No documents in this category yet.</div>;
  }
  return (
    <ul className="divide-y divide-border">
      {items.map(d => (
        <li key={d.id} className="py-3 flex items-start gap-3">
          <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">{d.title}</div>
            <div className="text-xs text-muted-foreground">{d.description}</div>
            <Badge variant="outline" className="text-[10px] mt-1">{d.doc_type}</Badge>
          </div>
        </li>
      ))}
    </ul>
  );
}
